// ╔══════════════════════════════════════════════════════════════════╗
// ║                   ADMIN DASHBOARD HOME                           ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Users,
  CreditCard,
  DollarSign,
  TrendingUp,
  Landmark,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  RefreshCw
} from 'lucide-react'
import { DashboardAPI } from '../../services/adminApi'

export default function DashboardHome() {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [health, setHealth] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      const [statsRes, healthRes] = await Promise.all([
        DashboardAPI.getStats(),
        DashboardAPI.getHealth()
      ])

      if (statsRes.success) {
        setStats(statsRes.data)
      }
      if (healthRes.success) {
        setHealth(healthRes.data)
      }
    } catch (err) {
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-400 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const statCards = [
    {
      title: 'Total Users',
      value: formatNumber(stats?.stats?.users?.total_users),
      subValue: `${formatNumber(stats?.stats?.users?.new_users_30d)} new this month`,
      icon: Users,
      color: 'blue',
      trend: 'up'
    },
    {
      title: 'Total Accounts',
      value: formatNumber(stats?.stats?.accounts?.total_accounts),
      subValue: `${formatNumber(stats?.stats?.accounts?.active_accounts)} active`,
      icon: CreditCard,
      color: 'emerald',
      trend: 'up'
    },
    {
      title: 'Total Balance',
      value: formatCurrency(stats?.stats?.accounts?.total_balance),
      subValue: 'Across all accounts',
      icon: DollarSign,
      color: 'violet',
      trend: 'up'
    },
    {
      title: 'Active Loans',
      value: formatNumber(stats?.stats?.loans?.active_loans),
      subValue: formatCurrency(stats?.stats?.loans?.outstanding_balance) + ' outstanding',
      icon: Landmark,
      color: 'orange',
      trend: 'neutral'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Welcome to Swift Gold Fiduciary Admin Panel</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-slate-800 border border-slate-700 rounded-xl p-5"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${stat.color}-500/20`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-400`} />
                </div>
                {stat.trend === 'up' && (
                  <span className="flex items-center gap-1 text-emerald-400 text-sm">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                )}
                {stat.trend === 'down' && (
                  <span className="flex items-center gap-1 text-red-400 text-sm">
                    <ArrowDownRight className="w-4 h-4" />
                  </span>
                )}
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">{stat.value}</h3>
              <p className="text-sm text-slate-400">{stat.title}</p>
              <p className="text-xs text-slate-500 mt-1">{stat.subValue}</p>
            </motion.div>
          )
        })}
      </div>

      {/* Secondary Stats & Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Items */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-400" />
            Pending Actions
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">Loan Applications</span>
              <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-sm font-semibold">
                {health?.pendingItems?.pending_loans || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">KYC Verifications</span>
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm font-semibold">
                {health?.pendingItems?.pending_kyc || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">Support Tickets</span>
              <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-sm font-semibold">
                {health?.pendingItems?.open_tickets || 0}
              </span>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-400" />
            System Health
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">Database</span>
              <span className={`flex items-center gap-1 ${
                health?.health?.database === 'healthy' ? 'text-emerald-400' : 'text-red-400'
              }`}>
                <CheckCircle className="w-4 h-4" />
                {health?.health?.database || 'unknown'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">API Server</span>
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle className="w-4 h-4" />
                {health?.health?.api || 'healthy'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">Active Sessions</span>
              <span className="text-white font-semibold">
                {health?.activeSessions || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Transaction Summary */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            Transactions (30 days)
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">Total Transactions</span>
              <span className="text-white font-semibold">
                {formatNumber(stats?.stats?.transactions?.total_transactions)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">Total Deposits</span>
              <span className="text-emerald-400 font-semibold">
                {formatCurrency(stats?.stats?.transactions?.total_deposits)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-400">Total Withdrawals</span>
              <span className="text-red-400 font-semibold">
                {formatCurrency(stats?.stats?.transactions?.total_withdrawals)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Admin Activity</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Admin</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Action</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Resource</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Time</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recentActivity?.length > 0 ? (
                stats.recentActivity.map((activity, index) => (
                  <tr key={index} className="border-b border-slate-700/50">
                    <td className="py-3 px-4">
                      <span className="text-white">
                        {activity.first_name} {activity.last_name}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                        {activity.action}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-sm">
                      {activity.resource_type || '-'}
                    </td>
                    <td className="py-3 px-4 text-slate-500 text-sm">
                      {new Date(activity.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-slate-500">
                    No recent activity
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
